import React, { useEffect, useRef, useState } from "react";
import { api, API } from "../../lib/api";
import { Button } from "../ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";

/**
 * Minimal SCORM 1.2 RTE bridge. The course content (served in the iframe)
 * walks up window.parent looking for an object exposing LMSInitialize etc —
 * since this component IS the direct parent of the iframe, setting
 * window.API here satisfies that lookup immediately (long before the
 * iframe's network fetch + script execution could possibly complete).
 */
export default function ScormPlayer({ assignmentId, onBack }) {
  const [launch, setLaunch] = useState(null);
  const cmiRef = useRef({});

  useEffect(() => {
    api.get(`/employee-portal/trainings/${assignmentId}/launch`).then((r) => {
      cmiRef.current = { ...(r.data.cmi || {}) };
      setLaunch(r.data);
    });
  }, [assignmentId]);

  useEffect(() => {
    if (!launch) return;
    const sync = (finish) => {
      api.post(`/employee-portal/trainings/${assignmentId}/progress`, { cmi: cmiRef.current, finish }).catch(() => {});
    };
    window.API = {
      LMSInitialize: () => "true",
      LMSFinish: () => { sync(true); return "true"; },
      LMSGetValue: (key) => (cmiRef.current[key] != null ? String(cmiRef.current[key]) : ""),
      LMSSetValue: (key, value) => { cmiRef.current[key] = value; return "true"; },
      LMSCommit: () => { sync(false); return "true"; },
      LMSGetLastError: () => "0",
      LMSGetErrorString: () => "",
      LMSGetDiagnostic: () => "",
    };
    return () => { delete window.API; };
  }, [launch, assignmentId]);

  if (!launch) return <div className="h-96 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-3" data-testid="portal-scorm-player">
      <div className="flex items-center justify-between gap-3">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-1.5" data-testid="portal-scorm-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to trainings
        </Button>
        <div className="text-[13px] font-semibold">{launch.course_title}</div>
      </div>
      <iframe
        title={launch.course_title}
        src={`${API}/training/content/${launch.course_id}/${launch.launch_path}`}
        className="w-full rounded-xl border border-border bg-white"
        style={{ height: "75vh" }}
        data-testid="portal-scorm-iframe"
      />
    </div>
  );
}
